/**
 * Custom Cart Script with Batched Updates
 * This script fetches cart data, updates cart items, and synchronizes core products and shipping insurance
 * in a batched manner to reduce API calls and improve performance.
 */

// Global sync flags and state variable.
let coresAreSynced = false;
let shippingSynced = false;
var shippingClicked = '';
let syncAttemptCount = 0;
// Keep this high for carts with packages
const MAX_SYNC_ATTEMPTS = 10;
// Track problematic items that fail to sync
let problematicItems = new Set();

// -------------------------------------------------------------------------
// Utility Functions
// -------------------------------------------------------------------------

/**
 * Fetches the current cart data from Shopify.
 * @returns {Promise<Object>} The cart JSON.
 */
async function fetchCartData() {
  try {
    const response = await fetch('/cart.js');
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart data:', error);
  }
}

/**
 * Updates a single cart item using the provided formData.
 * Triggers handleCartUpdates() after updating.
 * @param {FormData} formData - The update instructions.
 * @param {string} key - The unique key of the cart item.
 */
async function updateCartItem(formData, key) {
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    });
    await response.json(); // Wait for update response.
    handleCartUpdates();
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    location.reload();
  }
}

/**
 * Adds an addon (e.g., core charge or shipping insurance) to the cart.
 * Triggers handleCartUpdates() after adding.
 * @param {number|string} addonId - The variant ID of the addon.
 * @param {number} quantity - The quantity to add.
 * @param {string} parentTitle - The title of the parent product.
 * @param {number|string} parentId - The ID of the parent product.
 * @param {string} parentKey - The unique key of the parent product.
 */
function addAddonToCart(addonId, quantity, parentTitle, parentId, parentKey) {
  const formData = new FormData();
  formData.append('id', addonId);
  formData.append('quantity', quantity);
  formData.append('properties[Bundled with]', parentTitle);
  formData.append('properties[_parent_id]', parentId);
  formData.append('properties[_parent_key]', parentKey);

  fetch('/cart/add.js', {
    method: 'POST',
    body: formData,
  })
    .then(response => response.json())
    .then(data => {
      // If the variant cannot be found, remove the parent item.
      if (data.status === 422 && data.description && data.description.includes("Cannot find variant")) {
        console.error("Cannot find variant for addon. Removing parent with key:", parentKey);
        let removeFormData = new FormData();
        removeFormData.append(`updates[${parentKey}]`, 0);
        // Remove the parent, then continue syncing.
        return updateCartItem(removeFormData, parentKey);
      }
      // Otherwise, continue syncing.
      handleCartUpdates();
      return data;
    })
    .catch(error => console.error('Error adding addon to cart:', error));
}


/**
 * Batches multiple cart item updates into a single request.
 * @param {Object} updates - A mapping of item key to new quantity.
 */
async function batchUpdateCartItems(updates) {
  const formData = new FormData();
  Object.keys(updates).forEach(key => {
    formData.append(`updates[${key}]`, updates[key]);
  });

  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    });
    await response.json();
    handleCartUpdates();
  } catch (error) {
    console.error('Error in batch updating cart items:', error);
    location.reload();
  }
}

/**
 * Finds the applicable shipping variant based on the filtered cart total.
 * @param {number} cartTotal - The filtered cart total.
 * @returns {number|string|null} The shipping variant ID, or null if none found.
 */
function findApplicableShippingVariant(cartTotal) {
  const applicableVariant = shippingProducts.find((variant) => {
    const priceFirst = parseFloat(variant.priceFirst);
    const priceLast = parseFloat(variant.priceLast);
    return cartTotal >= priceFirst && cartTotal <= priceLast;
  });
  return applicableVariant ? applicableVariant.id : null;
}

// -------------------------------------------------------------------------
// Batch Sync Functions
// -------------------------------------------------------------------------

/**
 * Batches the synchronization of shipping insurance.
 * - Categorizes cart items in one pass.
 * - Computes a filtered cart total.
 * - Batches updates to remove or adjust shipping insurance items.
 * @param {boolean} addShipping - Whether shipping insurance should be active.
 */
async function checkShippingInsurance(addShipping) {
  try {
    const cart = await fetchCartData();
    shippingSynced = true;

    let normalItems = [];
    let insuranceItems = [];
    let excludedPrices = 0;

    // Categorize items and sum excluded prices in one pass.
    for (const item of cart.items) {
      if (item.product_type === 'Shipping_Insurance') {
        insuranceItems.push(item);
        excludedPrices += item.original_line_price;
      } else if (['Core Deposit', 'core-deposit', 'mws_fee_generated', 'Tune Files', 'TCM Tune Files', 'Gift Cards'].includes(item.product_type)) {
        excludedPrices += item.original_line_price;
      } else {
        normalItems.push(item);
      }
    }

    let cartTotalFiltered = null;
    let shippingVariantId = null;
    if (normalItems.length > 0) {
      const totalPrice = cart.original_total_price;
      cartTotalFiltered = (totalPrice - excludedPrices) / 100;
      shippingVariantId = findApplicableShippingVariant(cartTotalFiltered);
    }

    // If shipping insurance should be added.
    if (addShipping && normalItems.length && cartTotalFiltered) {
      if (insuranceItems.length === 0) {
        shippingSynced = false;
        // Missing insurance item; add it separately.
        await addAddonToCart(shippingVariantId, 1, '', '', '');
      } else {
        // Batch update insurance items concurrently.
        await Promise.all(
          insuranceItems.map(item => {
            let formData = new FormData();
            if (item.id !== shippingVariantId || insuranceItems.length > 1) {
              shippingSynced = false;
              formData.append(`updates[${item.key}]`, 0);
            } else if (item.quantity > 1) {
              shippingSynced = false;
              formData.append(`updates[${item.key}]`, 1);
            }
            return formData.has(`updates[${item.key}]`)
              ? updateCartItem(formData, item.key)
              : Promise.resolve();
          })
        );
      }
    } else if (insuranceItems.length) {
      // If shipping insurance should not be added, remove any that exist.
      await Promise.all(
        insuranceItems.map(item => {
          shippingSynced = false;
          let formData = new FormData();
          formData.append(`updates[${item.key}]`, 0);
          return updateCartItem(formData, item.key);
        })
      );
    }
  } catch (error) {
    console.error('Error fetching or processing shipping cart data:', error);
    shippingSynced = false;
  }
}


// Global flag to stop further sync attempts if a critical error occurs.
let stopCoreSync = false;

/**
 * Batches the synchronization of core products and their addons.
 * - Processes each core product to check for matching addons.
 * - Batches updates for quantity adjustments or removals.
 * - Removes orphaned fee-generated items.
 */
async function checkCoreProducts() {
  // If a critical error has already halted syncing, exit early.
  if (stopCoreSync) {
    console.error("Sync halted due to critical error.");
    return;
  }

  try {
    // Fetch the latest cart data from Shopify.
    const cart = await fetchCartData();
    // Assume the core products are synced until we find discrepancies.
    coresAreSynced = true;

    // Build a set of valid parent keys from items that are core products.
    // A core product is identified by having a _core_id property.
    const currentParentKeys = new Set(
      cart.items
        .filter(item => item.properties && item.properties._core_id)
        .map(item => item.key)
    );

    // Process each item in the cart concurrently.
    await Promise.all(
      cart.items.map(async productItem => {
        // If the item is a core product (i.e. a parent)...
        if (productItem.properties && productItem.properties._core_id) {
          // console.log('Processing core product:', productItem.key);

          // Check for an associated addon (child) by looking for an item
          // whose _parent_key matches this parent's unique key.
          const addonExists = cart.items.find(
            item =>
              item.properties &&
              item.properties._parent_key === productItem.key
          );

          // If no matching child is found...
          if (!addonExists) {
            // Mark that the cart isn't fully synced.
            coresAreSynced = false;
            console.warn(
              "Mismatch detected: Parent product has no matching addon.",
              {
                parentKey: productItem.key,
                coreId: productItem.properties._core_id,
                title: productItem.title,
                quantity: productItem.quantity
              }
            );
            // Add to problematic items
            problematicItems.add(productItem.key);
            // Attempt to add the corresponding addon for the parent.
            await addAddonToCart(
              productItem.properties._core_id, // The variant ID from the metafield
              productItem.quantity,              // Use the parent's quantity for the addon
              productItem.title,                 // Parent title (for display or logging)
              productItem.id,                    // Parent product ID
              productItem.key                    // Parent's unique line item key (used as _parent_key)
            );
          }
          // If a matching child exists but its quantity doesn't match the parent...
          else if (addonExists.quantity !== productItem.quantity) {
            coresAreSynced = false;
            console.warn(
              "Quantity mismatch detected between parent and addon.",
              {
                parentKey: productItem.key,
                parentQuantity: productItem.quantity,
                addonKey: addonExists.key,
                addonQuantity: addonExists.quantity
              }
            );
            // Add both items to problematic items
            problematicItems.add(productItem.key);
            problematicItems.add(addonExists.key);
            // Prepare an update to set the addon's quantity to match the parent's.
            let formData = new FormData();
            formData.append(`updates[${addonExists.key}]`, productItem.quantity);
            await updateCartItem(formData, addonExists.key);
          }
        }
        // If the item is not a core product, but it has a _parent_key,
        // it is considered a child. We need to check if its parent still exists.
        else if (productItem.properties._parent_key && !currentParentKeys.has(productItem.properties._parent_key)) {
          coresAreSynced = false;
          console.warn(
            "Orphaned fee-generated item detected: Missing or invalid parent key.",
            {
              productKey: productItem.key,
              parentKey: productItem.properties._parent_key || "None"
            }
          );
          // Add to problematic items
          problematicItems.add(productItem.key);
          // Remove this orphaned child by setting its quantity to zero.
          let formData = new FormData();
          formData.append(`updates[${productItem.key}]`, 0);
          await updateCartItem(formData, productItem.key);
        }
      })
    );
  } catch (error) {
    console.error('Error fetching or processing cart data:', error);
    coresAreSynced = false;
  }
}

// -------------------------------------------------------------------------
// Local Storage and UI Functions
// -------------------------------------------------------------------------

/**
 * Toggles the shipping insurance status in local storage.
 * @returns {boolean} The new state.
 */
function toggleShippingStatus() {
  const key = 'Include Shipping Insurance';
  const currentState = localStorage.getItem(key) === 'true';
  const newState = !currentState;
  localStorage.setItem(key, newState.toString());
  return newState;
}

/**
 * Retrieves the current shipping insurance status from local storage.
 * @returns {boolean} The shipping insurance status.
 */
function getShippingStatus() {
  const key = 'Include Shipping Insurance';
  return localStorage.getItem(key) === 'true';
}

/**
 * Disables the cart drawer and checkout button to prevent multiple actions during syncing.
 */
function disableCartDrawer() {
  const cartDrawer = document.querySelector('.main-cart');
  if (!cartDrawer) {
    return;
  }

  const cartCheckoutButton = cartDrawer.querySelector('.checkout-button');
  if (cartCheckoutButton && window.location.pathname === '/cart' && window.wholesale) {
    cartCheckoutButton.classList.add('loading');
  }
  cartDrawer.setAttribute('disabled', '');
}

/**
 * Re-enables the cart drawer and removes loading state from the checkout button.
 */
async function enableCartDrawer() {
  const cartDrawer = document.querySelector('.main-cart');
  if (!cartDrawer) {
    return;
  }
  const cartCheckoutButton = cartDrawer.querySelector('.checkout-button');
  setTimeout(() => {
    cartDrawer.removeAttribute('disabled');
    if (window.location.pathname === '/cart') {
      cartCheckoutButton.classList.remove('loading');
    }
  }, 250);
}

/**
 * Clears all items from the cart.
 */
async function clearCart() {
  try {
    const formData = new FormData();
    formData.append('updates', '{}');
    await fetch('/cart/clear.js', {
      method: 'POST',
      body: formData
    });
    // location.reload();
  } catch (error) {
    console.error('Error clearing cart:', error);
  }
}

// -------------------------------------------------------------------------
// Main Update Handler and Event Listeners
// -------------------------------------------------------------------------

/**
 * Handles overall cart updates by synchronizing core products and shipping insurance.
 * Refreshes the UI if both core and shipping sync statuses are good.
 */
async function handleCartUpdates() {
  disableCartDrawer();
  await checkCoreProducts();
  if (coresAreSynced) {
    const addShipping = getShippingStatus();
    await checkShippingInsurance(addShipping);
  }
  if (coresAreSynced && shippingSynced) {
    // console.log('Cores and Shipping Synced. Refreshing Cart.')
    syncAttemptCount = 0; // Reset counter on successful sync
    problematicItems.clear(); // Clear problematic items on successful sync
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.refresh === 'function') {
      cartItemsEl.refresh();
    }
    enableCartDrawer();
  } else {
    // console.log('Cart not fully synced, updates and refreshes withheld. Checking again.');
    syncAttemptCount++;

    if (syncAttemptCount >= MAX_SYNC_ATTEMPTS) {
      console.error('Maximum sync attempts reached. Attempting to remove problematic items...');

      // If we have problematic items, try removing them first
      if (problematicItems.size > 0) {
        try {
          const formData = new FormData();
          let hasShippingInsurance = false;

          // Check if any problematic items are shipping insurance variants
          const cart = await fetchCartData();
          problematicItems.forEach(key => {
            const item = cart.items.find(item => item.key === key);
            if (item && item.product_type === 'Shipping_Insurance') {
              hasShippingInsurance = true;
            } else {
              formData.append(`updates[${key}]`, 0);
            }
          });

          // If we found shipping insurance issues, disable it
          if (hasShippingInsurance) {
            // console.log('Disabling shipping insurance due to sync issues');
            localStorage.setItem('Include Shipping Insurance', 'false');
            formData.append('attributes[include_shipping_insurance]', 'false');
          }

          // Only proceed with the update if we have items to remove
          if (formData.has('updates')) {
            await fetch('/cart/update.js', {
              method: 'POST',
              body: formData
            });
          }

          // Reset counters and try syncing again
          syncAttemptCount = 0;
          problematicItems.clear();
          handleCartUpdates();
          return;
        } catch (error) {
          console.error('Failed to remove problematic items:', error);
        }
      }

      // If removing problematic items failed or there were none, clear the cart
      console.error('Removing problematic items failed or no problematic items found. Clearing cart...');
      await clearCart();
    }
  }
}

/**
 * Adds an event listener for clicks on the shipping toggle button.
 * Toggles local storage, updates the cart attribute, and refreshes the UI.
 */
async function clickShippingToggle() {
  disableCartDrawer();
  const cartDrawer = document.querySelector('.main-cart');
  if (cartDrawer) {
    cartDrawer.addEventListener('click', function (event) {
      const shippingButton = event.target.closest('.shipping-check');
      if (shippingButton) {
        shippingClicked = true;
        disableCartDrawer();
        toggleShippingStatus();
        const addShipping = getShippingStatus();

        const formData = new FormData();
        formData.append('attributes[include_shipping_insurance]', addShipping.toString());
        updateCartItem(formData, '');
        if (addShipping) {
          shippingButton.classList.add('checked');
        } else {
          shippingButton.classList.remove('checked');
        }
      }
    });
  }
}

// Initialize shipping toggle and update shipping attribute on DOM load.
function initCartLogic() {
  clickShippingToggle();

  const addShipping = getShippingStatus();
  const formData = new FormData();
  formData.append('attributes[include_shipping_insurance]', addShipping.toString());
  updateCartItem(formData, '');
}

if (window.location.pathname === '/cart') {
  // On the /cart page, run on DOMContentLoaded only
  document.addEventListener('DOMContentLoaded', () => {
    initCartLogic();
  });
} else {
  // On all other pages, run when cart drawer opens
  document.addEventListener('on:cart-drawer:before-open', (event) => {
    initCartLogic();
  });
}

// Listen for the custom "on:line-item:change:end" event to trigger cart updates.
document.addEventListener('on:line-item:change:end', () => {
  // console.log('on:line-item:change:end detected');
  handleCartUpdates();
});
// Listen for cart drawer disable/enable events from other components
document.addEventListener('on:cart-drawer:disable', () => {
  disableCartDrawer();
});

document.addEventListener('on:cart-drawer:enable', () => {
  enableCartDrawer();
});

// -------------------------------------------------------------------------
// Clear Cart Button
// -------------------------------------------------------------------------

document.addEventListener('click', function (e) {
  if (e.target.matches('.js-clear-cart-debug')) {

    // Disable drawer while updating
    document.dispatchEvent(new CustomEvent('on:cart-drawer:disable', { bubbles: true }));

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const updates = {};
        cart.items.forEach(item => updates[item.id] = 0);

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      })
      .then(r => r.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('on:cart-drawer:enable', { bubbles: true }));
      });
  }
});